/**
 * theory-engine → VexFlow adapters.
 *
 * Pure data transforms. No DOM, no VexFlow imports — these functions
 * exist so the music-theory layer can describe a score without owning
 * the rendering pipeline. Score.tsx is the only piece that touches
 * VexFlow at runtime.
 *
 * VexFlow key format: `"<letter><accidental>/<octave>"`, lowercase.
 *   c/4    → middle C
 *   c#/4   → C-sharp middle
 *   bb/3   → B-flat below middle C
 *   f##/5  → F-double-sharp octave above middle C
 */

import {
  type Pitch,
  type Accidental,
  toMidi,
} from "@music-app/theory-engine";
import {
  type Chord,
  chordVoicing,
} from "@music-app/theory-engine";
import {
  type Scale,
  scalePitches,
} from "@music-app/theory-engine";

import type { VexNote, VexDuration } from "./notation-types.js";

const ACCIDENTAL_TO_VEX: Record<Accidental, string> = {
  [-2]: "bb",
  [-1]: "b",
  [0]: "",
  [1]: "#",
  [2]: "##",
};

const DEFAULT_DURATION: VexDuration = "q";

/**
 * Format a single Pitch as a VexFlow key string. Used internally by the
 * three exported adapters; exposed because the Score component also
 * needs it for parsing accidentals back out.
 */
export function pitchToVexKey(pitch: Pitch): string {
  const letter = pitch.letter.toLowerCase();
  const acc = ACCIDENTAL_TO_VEX[pitch.accidental];
  return `${letter}${acc}/${pitch.octave}`;
}

/**
 * Single pitch as a VexNote. The `keys` array has exactly one entry.
 */
export function pitchToVexNote(
  pitch: Pitch,
  opts?: { duration?: VexDuration },
): VexNote {
  return {
    keys: [pitchToVexKey(pitch)],
    duration: opts?.duration ?? DEFAULT_DURATION,
  };
}

/**
 * Stack a chord's pitches into one VexNote. Uses {@link chordVoicing}
 * so inversion is preserved — bass note at the bottom, upper voices
 * stacked above. Keys are sorted strictly ascending by MIDI so VexFlow
 * gets a consistent low-to-high stack regardless of how the chord was
 * constructed.
 */
export function chordToVexChord(
  chord: Chord,
  opts?: { duration?: VexDuration },
): VexNote {
  const voiced = chordVoicing(chord);
  const sorted = [...voiced].sort((a, b) => toMidi(a) - toMidi(b));
  return {
    keys: sorted.map(pitchToVexKey),
    duration: opts?.duration ?? DEFAULT_DURATION,
  };
}

/**
 * One VexNote per scale degree. Does not wrap to the octave — callers
 * that want the closing tonic should append it themselves.
 */
export function scaleToVexLine(
  scale: Scale,
  opts?: { duration?: VexDuration },
): VexNote[] {
  const duration = opts?.duration ?? DEFAULT_DURATION;
  return scalePitches(scale).map((p) => ({
    keys: [pitchToVexKey(p)],
    duration,
  }));
}
